<template>
  <a-modal
    :visible="visible"
    :footer="null"
    :width="560"
    wrapClassName="dispatch-order-detail-modal"
    @cancel="$emit('cancel')"
  >
    <template slot="title">
      <div class="modal-title-wrap">
        <span class="modal-title">订单详情</span>
        <span v-if="order" class="modal-order-no">{{ order.orderNo }}</span>
      </div>
    </template>

    <a-spin :spinning="loading">
      <div v-if="order" class="detail-body">
        <div class="detail-status-row">
          <span class="status-pill" :class="'status-' + (order.status || 'pending')">
            {{ order.statusLabel }}
          </span>
          <span v-if="order.assignModeLabel" class="status-pill" :class="'assign-' + (order.assignMode || 'assigned')">
            {{ order.assignModeLabel }}
          </span>
        </div>

        <div class="info-grid">
          <div class="info-card">
            <div class="info-icon hospital"><a-icon type="medicine-box" /></div>
            <div>
              <p class="info-label">医院信息</p>
              <p class="info-main">{{ order.hospital }}</p>
              <p class="info-sub">{{ order.department }} · {{ order.bedNo }}</p>
            </div>
          </div>
          <div class="info-card">
            <div class="info-icon patient"><a-icon type="user" /></div>
            <div>
              <p class="info-label">患者信息</p>
              <p class="info-main">{{ order.patientName }}</p>
              <p class="info-sub">{{ order.patientAge }}岁 · {{ order.patientGender }} · {{ order.careLevel }}</p>
            </div>
          </div>
          <div class="info-card">
            <div class="info-icon time"><a-icon type="calendar" /></div>
            <div>
              <p class="info-label">服务时间</p>
              <p class="info-main">{{ order.dateRange }}</p>
              <p class="info-sub">提交于 {{ order.submitTime }}</p>
            </div>
          </div>
          <div class="info-card">
            <div class="info-icon caregiver"><a-icon type="team" /></div>
            <div>
              <p class="info-label">护工安排</p>
              <p v-if="order.caregiverName" class="info-main">已指派：{{ order.caregiverName }}</p>
              <p v-else-if="order.preferredCaregiverName" class="info-main">
                意向护工：{{ order.preferredCaregiverName }}（待确认）
              </p>
              <p v-else class="info-main">随机分配，待调度安排</p>
              <p v-if="order.assignOperatorName" class="info-sub">
                指派账号：{{ order.assignOperatorName }}<template v-if="order.assignTime"> · {{ order.assignTime }}</template>
              </p>
            </div>
          </div>
        </div>

        <!-- 用户评价 -->
        <div v-if="hasReview" class="review-block">
          <div class="review-head">
            <a-icon type="star" theme="filled" class="review-head-icon" />
            <span>用户评价</span>
            <span v-if="order.review.createTime" class="review-time">{{ order.review.createTime }}</span>
          </div>
          <div class="review-rows">
            <div class="review-row">
              <span class="review-dim">综合满意度</span>
              <div class="star-line">
                <a-icon
                  v-for="n in 5"
                  :key="'s' + n"
                  type="star"
                  theme="filled"
                  :class="n <= (order.review.starRating || 0) ? 'star-on' : 'star-off'"
                />
                <span class="star-num">{{ order.review.starRating || 0 }}</span>
              </div>
            </div>
            <div v-if="order.review.professionalismRating" class="review-row">
              <span class="review-dim">专业程度</span>
              <div class="star-line">
                <a-icon
                  v-for="n in 5"
                  :key="'p' + n"
                  type="star"
                  theme="filled"
                  :class="n <= order.review.professionalismRating ? 'star-on' : 'star-off'"
                />
                <span class="star-num">{{ order.review.professionalismRating }}</span>
              </div>
            </div>
            <div v-if="order.review.attitudeRating" class="review-row">
              <span class="review-dim">服务态度</span>
              <div class="star-line">
                <a-icon
                  v-for="n in 5"
                  :key="'a' + n"
                  type="star"
                  theme="filled"
                  :class="n <= order.review.attitudeRating ? 'star-on' : 'star-off'"
                />
                <span class="star-num">{{ order.review.attitudeRating }}</span>
              </div>
            </div>
          </div>
          <p class="review-content">{{ order.review.content || "用户未填写文字评价" }}</p>
        </div>

        <div v-else-if="order.status === 'done'" class="detail-tip done-tip">
          <a-icon type="info-circle" />
          订单已完工，用户可在家属端「我的订单」中评价护工。
        </div>
        <div v-else-if="order.canComplete" class="detail-tip">
          <a-icon type="clock-circle" />
          服务结束后可点击「标记已完工」，用户即可进行评价。
        </div>
      </div>
    </a-spin>
  </a-modal>
</template>

<script>
export default {
  name: "DispatchOrderDetailModal",
  props: {
    visible: { type: Boolean, default: false },
    order: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },
  computed: {
    hasReview() {
      if (!this.order) return false;
      if (this.order.reviewed && this.order.review) return true;
      const r = this.order.review;
      return !!(r && (r.starRating || r.professionalismRating || r.attitudeRating || r.content));
    },
  },
};
</script>

<style scoped>
.modal-title-wrap {
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
}
.modal-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #0f172a;
}
.modal-order-no {
  font-size: 0.8125rem;
  color: #94a3b8;
  font-family: monospace;
}
.detail-body {
  padding-top: 0.25rem;
}
.detail-status-row {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}
.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.625rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.5;
}
.status-pending { color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; }
.status-pending_confirm { color: #ea580c; background: #fff7ed; border: 1px solid #fed7aa; }
.status-serving { color: #16a34a; background: #f0fdf4; border: 1px solid #bbf7d0; }
.status-done { color: #1d4ed8; background: #eff6ff; border: 1px solid #bfdbfe; }
.assign-preferred { color: #c2410c; background: #fff7ed; border: 1px solid #fed7aa; }
.assign-random { color: #0369a1; background: #f0f9ff; border: 1px solid #bae6fd; }
.assign-assigned { color: #15803d; background: #f0fdf4; border: 1px solid #bbf7d0; }

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-bottom: 1rem;
}
.info-card {
  display: flex;
  gap: 0.75rem;
  padding: 0.875rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
}
.info-icon {
  width: 36px;
  height: 36px;
  border-radius: 0.625rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1rem;
}
.info-icon.hospital { background: #eff6ff; color: #2563eb; }
.info-icon.patient { background: #fdf4ff; color: #a855f7; }
.info-icon.time { background: #fff7ed; color: #ea580c; }
.info-icon.caregiver { background: #f0fdf4; color: #16a34a; }
.info-label {
  margin: 0;
  font-size: 0.6875rem;
  color: #94a3b8;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.info-main {
  margin: 0.25rem 0 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.4;
}
.info-sub {
  margin: 0.125rem 0 0;
  font-size: 0.75rem;
  color: #64748b;
}

.review-block {
  background: linear-gradient(135deg, #fffbeb 0%, #fff7ed 100%);
  border: 1px solid #fde68a;
  border-radius: 0.875rem;
  padding: 1rem 1.125rem;
}
.review-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  color: #92400e;
  margin-bottom: 0.875rem;
}
.review-head-icon {
  color: #f59e0b;
}
.review-time {
  margin-left: auto;
  font-size: 0.75rem;
  font-weight: 400;
  color: #b45309;
}
.review-rows {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
.review-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}
.review-dim {
  font-size: 0.8125rem;
  color: #78350f;
  flex-shrink: 0;
}
.star-line {
  display: flex;
  align-items: center;
  gap: 0.125rem;
}
.star-on { color: #f59e0b; font-size: 0.875rem; }
.star-off { color: #e5e7eb; font-size: 0.875rem; }
.star-num {
  margin-left: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #b45309;
}
.review-content {
  margin: 0;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.75);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: #44403c;
  line-height: 1.65;
}

.detail-tip {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 0.25rem;
  padding: 0.75rem 1rem;
  border-radius: 0.625rem;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  font-size: 0.8125rem;
  color: #1d4ed8;
  line-height: 1.5;
}
.done-tip {
  background: #f0fdf4;
  border-color: #bbf7d0;
  color: #15803d;
}

@media (max-width: 520px) {
  .info-grid {
    grid-template-columns: 1fr;
  }
}
</style>
